import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";

const eur = (c) => `€${(c / 100).toLocaleString("en-US")}`;
const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [status, setStatus] = useState("checking"); // checking | paid | pending | error
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let timer;

    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setOrder(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) {
            clearCart();
            cleared.current = true;
          }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setStatus("error");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_POLLS) {
          setStatus("pending");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch {
        setStatus("error");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clearCart]);

  return (
    <div className="min-h-[85svh] flex items-center justify-center pt-32 pb-24" data-testid="payment-success-page">
      <div className="container-lux max-w-xl text-center">
        {status === "checking" && (
          <>
            <p className="label-mono text-mineral mb-4">Confirming your order</p>
            <div className="w-10 h-10 border border-amethyst border-t-transparent rounded-full animate-spin mx-auto" />
          </>
        )}

        {status === "paid" && order && (
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="label-mono text-amethyst mb-6">Payment confirmed</p>
            <h1 className="heading-serif text-4xl md:text-5xl mb-8 leading-tight">
              Your order is confirmed
            </h1>
            <div className="border border-mineral/25 p-8 mb-10 text-left grain relative">
              <p className="label-mono text-mineral mb-2">Order Reference</p>
              <p className="font-mono text-bronze text-lg tracking-[0.2em] mb-6" data-testid="batch-number">
                № {order.batch_number}
              </p>
              <div className="space-y-2 border-t border-mineral/20 pt-5">
                {order.items?.map((i) => (
                  <div key={i.slug} className="flex justify-between text-sm">
                    <span>
                      {i.name} ×{i.quantity}
                    </span>
                    <span className="font-mono">{eur(i.unit_amount * i.quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between border-t border-mineral/20 pt-5 mt-5">
                <span className="label-mono">Total</span>
                <span className="font-serif text-xl">{eur(order.amount)}</span>
              </div>
            </div>
            <p className="text-alabaster/70 mb-10 leading-relaxed">
              A confirmation email is on its way. Your order will be carefully wrapped and shipped from the
              Netherlands within three working days.
            </p>
            <Link
              to="/collection"
              data-testid="continue-shopping"
              className="btn-relic border-alabaster bg-alabaster text-charcoal hover:bg-amethyst hover:border-amethyst"
            >
              Continue Shopping
            </Link>
          </motion.div>
        )}

        {status === "pending" && (
          <>
            <p className="label-mono text-bronze mb-6">Payment processing</p>
            <h1 className="heading-serif text-3xl md:text-4xl tracking-wide mb-6">Still confirming…</h1>
            <p className="text-alabaster/70 mb-8">
              Your payment is being verified. Check your account shortly for confirmation.
            </p>
            <Link to="/account" className="btn-relic border-alabaster/40 text-alabaster hover:border-amethyst">
              View Account
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <p className="label-mono text-destructive mb-6">Something went astray</p>
            <h1 className="heading-serif text-3xl md:text-4xl tracking-wide mb-6">
              We could not confirm this order
            </h1>
            <Link to="/collection" className="btn-relic border-alabaster/40 text-alabaster hover:border-amethyst">
              Return to the Collection
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
